import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  Scale, Upload, FileText, Loader2, Trash2, Eye, AlertTriangle, ScanLine,
  CheckCircle, Clock, RefreshCw, Settings as SettingsIcon, Gavel,
  TrendingUp, Sparkles, Building2, User as UserIcon, FolderOpen,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { ExpedienteSelector } from '../components/ExpedienteSelector';
import { toast } from '../hooks/use-toast';
import { obtenerToken } from '../lib/auth';

const API = process.env.REACT_APP_BACKEND_URL;
const TIPO = 'ca_hipotecaria';

const ESTADO_COLOR = {
  subido: 'bg-gray-100 text-gray-700',
  convirtiendo: 'bg-blue-100 text-blue-700',
  convertido: 'bg-blue-100 text-blue-800',
  sin_ocr: 'bg-orange-100 text-orange-800',
  analizando: 'bg-indigo-100 text-indigo-700',
  analizado: 'bg-green-100 text-green-800',
  error: 'bg-red-100 text-red-800',
  descartado: 'bg-gray-100 text-gray-500',
  expediente_creado: 'bg-emerald-100 text-emerald-800',
  expediente_anadido: 'bg-emerald-100 text-emerald-800',
  extrajudicial_creado: 'bg-emerald-100 text-emerald-800',
  pausado: 'bg-yellow-100 text-yellow-800',
};

const ESTADO_LABEL = {
  subido: 'Subido',
  convirtiendo: 'Preparando documento',
  convertido: 'Listo para analizar',
  sin_ocr: 'OCR no resuelto',
  analizando: 'Analizando con IA…',
  analizado: 'Analizado',
  error: 'Error',
  descartado: 'Descartado',
  expediente_creado: 'Expediente creado',
  expediente_anadido: 'Añadido a expediente',
  extrajudicial_creado: 'Reclamación creada',
  pausado: 'En pausa',
};

export const BANDA_COLOR = {
  MUY_ALTA: 'bg-emerald-600 text-white',
  ALTA: 'bg-green-600 text-white',
  MEDIA: 'bg-amber-500 text-white',
  BAJA: 'bg-orange-500 text-white',
  MUY_BAJA: 'bg-red-600 text-white',
};

const formatEur = (v) => new Intl.NumberFormat('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 }).format(v || 0);

export default function AnalisisCAHipotecaria() {
  const navigate = useNavigate();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [providers, setProviders] = useState(null);
  const [modulo, setModulo] = useState(null);
  const [providerSel, setProviderSel] = useState('anthropic');
  const [modelSel, setModelSel] = useState('claude-opus-4-7');
  const [expediente, setExpediente] = useState(null);
  const [pendingAuto, setPendingAuto] = useState({});

  const tokenHeader = () => ({ headers: { Authorization: `Bearer ${obtenerToken()}` } });

  const loadList = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/api/hipotecas/analisis?tipo=${TIPO}`, tokenHeader());
      setItems(r.data);
    } catch (e) {
      toast({ title: 'Error', description: 'No se pudo cargar la lista', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadConfig = useCallback(async () => {
    try {
      const [rp, rm] = await Promise.all([
        axios.get(`${API}/api/hipotecas/providers`, tokenHeader()),
        axios.get(`${API}/api/hipotecas/modulos`, tokenHeader()),
      ]);
      setProviders(rp.data);
      const cfg = (rm.data || []).find((m) => m.modulo === TIPO);
      if (cfg) {
        setModulo(cfg);
        setProviderSel(cfg.provider);
        setModelSel(cfg.model);
      }
    } catch (e) {
      console.warn('config', e);
    }
  }, []);

  useEffect(() => { loadList(); loadConfig(); }, [loadList, loadConfig]);

  useEffect(() => {
    const pendingIds = Object.keys(pendingAuto).map((s) => parseInt(s, 10));
    if (pendingIds.length > 0) {
      const item = items.find((i) => pendingIds.includes(i.id) && i.estado === 'convertido');
      if (item) {
        const cfg = pendingAuto[item.id];
        setPendingAuto((prev) => { const c = { ...prev }; delete c[item.id]; return c; });
        axios.post(`${API}/api/hipotecas/analisis/${item.id}/analizar`, cfg, tokenHeader())
          .then(() => {
            toast({ title: 'Análisis iniciado', description: `${item.nombre_archivo_original} — la IA está analizando la comisión de apertura.` });
            loadList();
          })
          .catch((err) => toast({ title: 'Error al iniciar análisis', description: err.response?.data?.detail || err.message, variant: 'destructive' }));
      }
      const fallidos = items.filter((i) => pendingIds.includes(i.id) && ['sin_ocr', 'error'].includes(i.estado));
      if (fallidos.length > 0) {
        setPendingAuto((prev) => { const c = { ...prev }; fallidos.forEach((f) => delete c[f.id]); return c; });
      }
    }
    const enProceso = items.some((i) => ['analizando', 'convirtiendo'].includes(i.estado));
    if (!enProceso) return;
    const t = setTimeout(loadList, 4000);
    return () => clearTimeout(t);
  }, [items, loadList, pendingAuto]);

  const handleFile = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 50 * 1024 * 1024) {
      toast({ title: 'Archivo demasiado grande', description: 'Máx 50 MB', variant: 'destructive' });
      return;
    }
    const fd = new FormData();
    fd.append('file', file);
    fd.append('tipo_analisis', TIPO);
    if (expediente) fd.append('expediente_id', expediente.id);
    setUploading(true);
    setUploadProgress(0);
    try {
      const r = await axios.post(`${API}/api/hipotecas/analisis`, fd, {
        ...tokenHeader(),
        headers: { ...tokenHeader().headers, 'Content-Type': 'multipart/form-data' },
        onUploadProgress: (evt) => {
          if (evt.total) setUploadProgress(Math.round((evt.loaded / evt.total) * 100));
        },
      });
      setPendingAuto((prev) => ({ ...prev, [r.data.id]: { provider: providerSel, model: modelSel } }));
      toast({
        title: 'Documento subido',
        description: 'Si el PDF es un escaneo se aplicará OCR automáticamente. El análisis arrancará al terminar.',
      });
      await loadList();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    } finally {
      setUploading(false);
      setUploadProgress(0);
      e.target.value = '';
    }
  };

  const lanzarAnalisis = async (id) => {
    try {
      await axios.post(`${API}/api/hipotecas/analisis/${id}/analizar`, { provider: providerSel, model: modelSel }, tokenHeader());
      toast({ title: 'Análisis iniciado' });
      navigate(`/expedientes/analisis-ca-hipotecaria/${id}`);
    } catch (err) {
      toast({ title: 'Error al iniciar análisis', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const reintentarOcr = async (id) => {
    try {
      await axios.post(`${API}/api/hipotecas/analisis/${id}/reintentar-ocr`, {}, tokenHeader());
      toast({ title: 'OCR relanzado', description: 'Se intentará OCR local y, si no está disponible, OCR por IA.' });
      await loadList();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  const borrar = async (id) => {
    if (!window.confirm('¿Borrar este análisis y sus archivos?')) return;
    try {
      await axios.delete(`${API}/api/hipotecas/analisis/${id}`, tokenHeader());
      toast({ title: 'Análisis eliminado' });
      await loadList();
    } catch (err) {
      toast({ title: 'Error', description: err.response?.data?.detail || err.message, variant: 'destructive' });
    }
  };

  return (
    <div className="p-6 space-y-6" data-testid="analisis-ca-hipotecaria-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <Scale className="w-7 h-7 text-indigo-600" />
            Análisis IA de CA Hipotecaria
          </h1>
          <p className="text-gray-600 mt-1">
            Informe de viabilidad de la reclamación de la <strong>comisión de apertura</strong> (controles de
            transparencia, solapamiento, devengo y proporcionalidad)
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => navigate('/configuracion/prompts-ia')} data-testid="btn-config-prompts">
            <SettingsIcon className="w-4 h-4 mr-2" />
            Prompt y modelo IA
          </Button>
          <Button variant="outline" onClick={loadList} data-testid="btn-refresh">
            <RefreshCw className="w-4 h-4 mr-2" />
            Actualizar
          </Button>
        </div>
      </div>

      <Card className="border-2 border-dashed border-indigo-300 bg-indigo-50/30">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="flex-1 min-w-[280px]">
              <h3 className="font-semibold text-gray-800 flex items-center gap-2">
                <Upload className="w-5 h-5 text-indigo-600" />
                Sube la escritura y documentación precontractual
              </h3>
              <p className="text-sm text-gray-600 mt-1">
                PDF o MD (también DOCX). Si el PDF no tiene OCR se convierte automáticamente.
                Máx 50 MB. El informe se podrá descargar en PDF, DOCX y MD.
              </p>
              {modulo && (
                <p className="text-xs text-indigo-700 mt-2">
                  Prompt activo: {modulo.prompt_es_de_fabrica ? 'versión de fábrica' : `versión ${modulo.prompt_version_activa}`}
                  {modulo.tiene_api_key_propia && ' · API key propia del despacho'}
                </p>
              )}
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              {providers && (
                <>
                  <Select value={providerSel} onValueChange={(v) => { setProviderSel(v); setModelSel(providers.modelos[v]?.[0] || ''); }}>
                    <SelectTrigger className="w-[170px]" data-testid="select-provider"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {Object.keys(providers.modelos).map((p) => (
                        <SelectItem key={p} value={p}>
                          {providers.display_names?.[p]
                            || (p === 'anthropic' ? 'Claude' : p === 'openai' ? 'OpenAI' : p === 'gemini' ? 'Gemini' : p)}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Select value={modelSel} onValueChange={setModelSel}>
                    <SelectTrigger className="w-[240px]" data-testid="select-model"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      {(providers.modelos[providerSel] || []).map((m) => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </>
              )}
            </div>
          </div>

          <div className="flex items-end gap-3 flex-wrap border-t border-indigo-200 pt-4">
            <div>
              <p className="text-xs font-medium text-gray-700 mb-1 flex items-center gap-1">
                <FolderOpen className="w-3.5 h-3.5" /> Asociar a expediente (opcional)
              </p>
              <ExpedienteSelector value={expediente} onChange={setExpediente} testId="ca-expediente" />
            </div>
            <label className="inline-flex">
              {uploading ? (
                <div
                  className="relative inline-flex items-center justify-center min-w-[210px] h-10 px-4 rounded-md overflow-hidden bg-indigo-100 text-white font-medium text-sm cursor-not-allowed"
                  data-testid="btn-upload-progress"
                >
                  <div
                    className="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-600 to-indigo-500 transition-all duration-200"
                    style={{ width: `${uploadProgress}%` }}
                  />
                  <span className="relative flex items-center gap-2 z-10 mix-blend-difference">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {uploadProgress < 100 ? `Subiendo ${uploadProgress}%` : 'Procesando…'}
                  </span>
                </div>
              ) : (
                <>
                  <Button asChild className="bg-indigo-600 hover:bg-indigo-700" data-testid="btn-upload">
                    <span className="cursor-pointer">
                      <Upload className="w-4 h-4 mr-2" />
                      Subir documento
                    </span>
                  </Button>
                  <input type="file" hidden accept=".pdf,.docx,.doc,.md" onChange={handleFile} data-testid="input-file" />
                </>
              )}
            </label>
          </div>
        </CardContent>
      </Card>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <Card data-testid="kpi-total">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Total análisis</p>
              <p className="text-2xl font-bold">{items.length}</p>
            </div>
            <Gavel className="w-8 h-8 text-indigo-400" />
          </CardContent>
        </Card>
        <Card data-testid="kpi-viables">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Viabilidad alta</p>
              <p className="text-2xl font-bold text-green-700">
                {items.filter((i) => ['MUY_ALTA', 'ALTA'].includes(i.viabilidad_banda)).length}
              </p>
            </div>
            <CheckCircle className="w-8 h-8 text-green-400" />
          </CardContent>
        </Card>
        <Card data-testid="kpi-recuperable">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">Reclamable total</p>
              <p className="text-2xl font-bold text-emerald-700">
                {formatEur(items.reduce((s, i) => s + (i.importe_total_recuperable || 0), 0))}
              </p>
            </div>
            <TrendingUp className="w-8 h-8 text-emerald-400" />
          </CardContent>
        </Card>
        <Card data-testid="kpi-en-proceso">
          <CardContent className="p-4 flex items-center justify-between">
            <div>
              <p className="text-xs text-gray-500">En proceso</p>
              <p className="text-2xl font-bold text-blue-700">
                {items.filter((i) => ['analizando', 'convirtiendo'].includes(i.estado)).length}
              </p>
            </div>
            <Loader2 className="w-8 h-8 text-blue-400" />
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardHeader><CardTitle className="text-lg">Análisis de comisión de apertura</CardTitle></CardHeader>
        <CardContent>
          {loading ? (
            <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-indigo-600" /></div>
          ) : items.length === 0 ? (
            <div className="text-center py-12">
              <FileText className="w-12 h-12 text-gray-300 mx-auto mb-3" />
              <p className="text-gray-500">Sube la primera escritura para analizar la comisión de apertura</p>
            </div>
          ) : (
            <div className="space-y-2" data-testid="lista-analisis-ca">
              {items.map((a) => (
                <div
                  key={a.id}
                  className="border rounded-lg p-4 hover:bg-gray-50 transition-colors flex items-center justify-between gap-3 flex-wrap"
                  data-testid={`item-analisis-${a.id}`}
                >
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 flex-wrap">
                      <FileText className="w-5 h-5 text-indigo-500 shrink-0" />
                      <span className="font-medium truncate" title={a.nombre_archivo_original}>
                        {a.nombre_archivo_original}
                      </span>
                      <Badge className={ESTADO_COLOR[a.estado] || 'bg-gray-100'}>
                        {['convirtiendo', 'analizando'].includes(a.estado) && <Loader2 className="w-3 h-3 mr-1 animate-spin inline" />}
                        {ESTADO_LABEL[a.estado] || a.estado}
                      </Badge>
                      {a.viabilidad_banda && (
                        <Badge className={BANDA_COLOR[a.viabilidad_banda] || 'bg-gray-500 text-white'}>
                          {a.viabilidad_banda.replace('_', ' ')} · {a.puntuacion_total} pts
                        </Badge>
                      )}
                    </div>
                    {a.paso_actual && ['analizando', 'convirtiendo'].includes(a.estado) && (
                      <p className="text-xs text-blue-700 mt-1 truncate" title={a.paso_actual}>{a.paso_actual}</p>
                    )}
                    <div className="text-xs text-gray-500 mt-1 flex flex-wrap gap-x-4">
                      <span><Clock className="w-3 h-3 inline mr-1" />{new Date(a.created_at).toLocaleString('es-ES')}</span>
                      {a.cliente_nombre && <span><UserIcon className="w-3 h-3 inline mr-1" />{a.cliente_nombre}</span>}
                      {a.banco_nombre && <span><Building2 className="w-3 h-3 inline mr-1" />{a.banco_nombre}</span>}
                      {a.expediente_numero && (
                        <span className="text-emerald-700 font-medium">
                          <FolderOpen className="w-3 h-3 inline mr-1" />{a.expediente_numero}
                        </span>
                      )}
                      {a.num_clausulas_detectadas > 0 && (
                        <span className="text-indigo-700 font-medium">
                          <AlertTriangle className="w-3 h-3 inline mr-1" />{a.num_clausulas_detectadas} causas
                        </span>
                      )}
                      {a.importe_total_recuperable > 0 && (
                        <span className="text-emerald-700 font-medium">{formatEur(a.importe_total_recuperable)} reclamable</span>
                      )}
                    </div>
                  </div>
                  <div className="flex gap-2">
                    {a.estado === 'convertido' && (
                      <Button size="sm" className="bg-indigo-600 hover:bg-indigo-700" onClick={() => lanzarAnalisis(a.id)} data-testid={`btn-analizar-${a.id}`}>
                        <Sparkles className="w-4 h-4 mr-1" />
                        Analizar
                      </Button>
                    )}
                    {a.estado === 'sin_ocr' && (
                      <Button size="sm" variant="outline" onClick={() => reintentarOcr(a.id)} data-testid={`btn-ocr-${a.id}`}>
                        <ScanLine className="w-4 h-4 mr-1" />
                        Reintentar OCR
                      </Button>
                    )}
                    <Button size="sm" variant="outline" onClick={() => navigate(`/expedientes/analisis-ca-hipotecaria/${a.id}`)} data-testid={`btn-ver-${a.id}`}>
                      <Eye className="w-4 h-4 mr-1" />
                      Ver
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => borrar(a.id)} data-testid={`btn-borrar-${a.id}`}>
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
